import type { Iso } from "./index.js";

export const OMNI_ACCOUNT_NUMBER_MIN = 100001;
export const OMNI_ACCOUNT_NUMBER_MAX = 999999;
export const OMNI_ACCOUNT_COUNTER_INITIAL = 100000;

export interface AccountsCounter {
  current: number;
}
export type AccountCounter = AccountsCounter;

export type AccountNumberState = "issued" | "unused" | "replaced";
export type AccountNumberVia = "customer_create" | "billing_approval";

export interface AccountNumberAllocation {
  number: string;
  autotaskCompanyId: number | null;
  state: AccountNumberState;
  via: AccountNumberVia;
  allocatedBy: string;
  allocatedAt: Iso;
  replacedBy: string | null;
}
export type AccountNumberRecord = AccountNumberAllocation;

export interface AccountNumberReplaceRequest {
  number: string;
  expectedUpdatedAt: Iso;
}

export type AccountNumberSource = "omni" | "rpp" | "other";

export function formatOmniAccountNumber(sequenceOrNumber: number | string): string {
  if (typeof sequenceOrNumber === "string") {
    const trimmed = sequenceOrNumber.trim();
    if (trimmed.startsWith("PW")) {
      const parsed = parseOmniAccountNumber(trimmed);
      return `PW${parsed}`;
    }
    const parsedInt = Number.parseInt(trimmed, 10);
    if (!Number.isInteger(parsedInt) || String(parsedInt) !== trimmed) {
      throw new Error(`Invalid Omni account number format: "${sequenceOrNumber}".`);
    }
    if (parsedInt < OMNI_ACCOUNT_NUMBER_MIN || parsedInt > OMNI_ACCOUNT_NUMBER_MAX) {
      throw new RangeError(
        `Omni account number sequence out of range: ${parsedInt}. Must be an integer between ${OMNI_ACCOUNT_NUMBER_MIN} and ${OMNI_ACCOUNT_NUMBER_MAX}.`,
      );
    }
    return `PW${parsedInt}`;
  }

  if (
    !Number.isInteger(sequenceOrNumber) ||
    sequenceOrNumber < OMNI_ACCOUNT_NUMBER_MIN ||
    sequenceOrNumber > OMNI_ACCOUNT_NUMBER_MAX
  ) {
    throw new RangeError(
      `Omni account number sequence out of range: ${sequenceOrNumber}. Must be an integer between ${OMNI_ACCOUNT_NUMBER_MIN} and ${OMNI_ACCOUNT_NUMBER_MAX}.`,
    );
  }
  return `PW${sequenceOrNumber}`;
}

export function parseOmniAccountNumber(value: string): number {
  const match = /^PW(\d{6})$/.exec(value.trim());
  if (!match) {
    throw new Error(`Invalid Omni account number format: "${value}". Expected PW followed by 6 digits.`);
  }
  const sequence = Number.parseInt(match[1]!, 10);
  if (sequence < OMNI_ACCOUNT_NUMBER_MIN || sequence > OMNI_ACCOUNT_NUMBER_MAX) {
    throw new RangeError(
      `Omni account number out of range: "${value}". Sequence must be between ${OMNI_ACCOUNT_NUMBER_MIN} and ${OMNI_ACCOUNT_NUMBER_MAX}.`,
    );
  }
  return sequence;
}

export function isOmniAccountNumber(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    parseOmniAccountNumber(value);
    return true;
  } catch {
    return false;
  }
}

export function accountNumberSource(number: string | null | undefined): AccountNumberSource {
  if (!number) return "other";
  const trimmed = number.trim();
  if (/^PW\d{6}$/.test(trimmed)) return "omni";
  if (/^\d+$/.test(trimmed)) return "rpp";
  return "other";
}

export function isRppAccountNumber(number: string | null | undefined): boolean {
  if (!number) return false;
  return /^\d{4,6}$/.test(number.trim());
}
